import { ChangeEvent, ReactNode, useState } from 'react';
import { Field, FieldProps, FieldArray, ErrorMessage, getIn, useFormikContext } from 'formik';
import { Select } from '@round/ui-kit';
import { wave } from '@round/api';
import CheckIcon from '@/ui/icons/CheckIcon';
import { CountryMultiSelect, CountrySelect, LocationStep, LocationValue, locationIdOf } from './LocationStep';
import { waveSelectStyles } from './waveSelectStyles';
import { ChipMultiSelect, ChipOption } from './ChipSelect';
import { PhoneField } from './PhoneField';
import {
    AccountValue,
    BundleOfferValue,
    SignupFormValues,
    emptyAccount,
    emptyBundleOffer,
    isBundleOfferEmpty,
} from './SignupWizard';
import { LanguageOption, filterLanguageOption } from './languageOptions';

type PlatformOption = { value: wave.CreatorApplicationPlatform; label: string };

const PLATFORM_OPTIONS: PlatformOption[] = [
    { value: 'TIKTOK', label: 'TikTok' },
    { value: 'INSTAGRAM', label: 'Instagram' },
    { value: 'YOUTUBE', label: 'YouTube' },
];

export const platformLabel = (value: wave.CreatorApplicationPlatform) =>
    PLATFORM_OPTIONS.find((o) => o.value === value)?.label ?? 'Social';

// Choice vocabularies — mirror app/wave/constants.py (ROU-873).
export const NOTIFICATION_CHANNEL_OPTIONS: ChipOption<wave.CreatorNotificationChannel>[] = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS / text' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'instagram_broadcast', label: 'Instagram Broadcast' },
];

// Sentinel option values agreed with app/wave/constants.py section configs; both
// reveal an extra text input when picked.
export const GENRE_OTHER_VALUE = 'other';
export const ARTIST_FAN_PAGE_NICHE_VALUE = 'artist_fan_page';

export const AUDIENCE_TERRITORIES_COUNT = 3;
// Mirrors MAX_ACCOUNT_NICHES in app/wave/constants.py, which enforces it on submit.
export const MAX_ACCOUNT_NICHES = 3;

// Mirror the CreatorBundleOffer check constraints; the backend rejects anything outside them.
export const MIN_BUNDLE_POSTS = 2;
export const MIN_BUNDLE_DURATION_DAYS = 1;
export const MAX_BUNDLE_DURATION_DAYS = 14;

export const RATE_CURRENCY_OPTIONS: { value: wave.CreatorApplicationRateCurrency; label: string }[] = [
    { value: 'USD', label: 'USD $' },
    { value: 'GBP', label: 'GBP £' },
    { value: 'EUR', label: 'EUR €' },
];

// The per-account section values mirrored by the "same for all my accounts" shortcut
// (everything except the account's identity: platform + handle).
const ACCOUNT_SECTION_FIELDS: (keyof AccountValue)[] = [
    'basedInCountry',
    'audienceTerritories',
    'editNiches',
    'fanPageArtist',
    'genres',
    'genresOther',
    'contentLanguages',
    'rate',
    'rateCurrency',
];

const accountSectionValues = (account: AccountValue) =>
    Object.fromEntries(ACCOUNT_SECTION_FIELDS.map((field) => [field, account[field]]));

export const AVAILABILITY_OPTIONS: ChipOption<wave.CreatorAvailability>[] = [
    { value: 'weekdays', label: 'Weekdays' },
    { value: 'evenings', label: 'Evenings' },
    { value: 'weekends', label: 'Weekends' },
];

export const FieldError = ({ name }: { name: string }) => (
    <ErrorMessage name={name} component="span" className="text-xs text-wm-danger" />
);

export const RequiredLabel = ({ children }: { children: ReactNode }) => (
    <span className="wm-field__label">
        <span>
            {children} <span className="text-wm-ink-3">*</span>
        </span>
    </span>
);

export const FieldLabel = ({ children }: { children: ReactNode }) => (
    <span className="mb-2 block text-sm font-medium text-wm-ink">{children}</span>
);

export const SummaryRow = ({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) => (
    <div className="flex items-start justify-between gap-3 border-b border-wm-line-2 py-2.5 last:border-b-0">
        <div className="min-w-0">
            <div className="text-xs text-wm-ink-3">{label}</div>
            <div className="text-sm text-wm-ink">{value || '—'}</div>
        </div>
        <button type="button" className="wm-btn wm-btn--link shrink-0" onClick={onEdit}>
            Edit
        </button>
    </div>
);

export const labelsOf = <TValue extends string>(options: ChipOption<TValue>[], values: TValue[]): string =>
    options
        .filter((o) => values.includes(o.value))
        .map((o) => o.label)
        .join(', ');

export const BasicsStep = () => (
    <div className="flex flex-col gap-4">
        <Field name="firstName">
            {({ field }: FieldProps) => (
                <label className="wm-field">
                    <RequiredLabel>First name</RequiredLabel>
                    <input className="wm-input" type="text" {...field} />
                    <FieldError name="firstName" />
                </label>
            )}
        </Field>
        <Field name="lastName">
            {({ field }: FieldProps) => (
                <label className="wm-field">
                    <RequiredLabel>Last name</RequiredLabel>
                    <input className="wm-input" type="text" {...field} />
                    <FieldError name="lastName" />
                </label>
            )}
        </Field>
        <Field name="email">
            {({ field }: FieldProps) => (
                <label className="wm-field">
                    <RequiredLabel>Email</RequiredLabel>
                    <input className="wm-input" type="email" {...field} />
                    <FieldError name="email" />
                </label>
            )}
        </Field>
        <Field name="confirmEmail">
            {({ field }: FieldProps) => (
                <label className="wm-field">
                    <RequiredLabel>Confirm email</RequiredLabel>
                    <input className="wm-input" type="email" {...field} />
                    <FieldError name="confirmEmail" />
                </label>
            )}
        </Field>
        <PhoneField />
        <div>
            <label className="wm-check">
                <Field type="checkbox" name="isOver18" />
                <span className="wm-check__box">
                    <CheckIcon />
                </span>
                <span className="wm-check__text">I&apos;m 18 or over. Wave is for creators aged 18 and above.</span>
            </label>
            <FieldError name="isOver18" />
        </div>
    </div>
);

export const AccountsStep = () => {
    const formik = useFormikContext<SignupFormValues>();

    // Removing the account leaves every bundle offer that referenced it holding a stale
    // uid: still non-empty, so it passes validation, and then resolves to no account and
    // falls through bundleOfferAccounts' "empty means all accounts" branch — binding the
    // bundle to accounts the applicant never picked. Dropping the uid here leaves that
    // fallback doing only its one job, covering the single account case where the picker
    // is hidden. Only removal prunes: uids are deliberately stable across handle edits.
    const pruneBundleOfferAccount = (uid: string) =>
        formik.setFieldValue(
            'bundleOffers',
            formik.values.bundleOffers.map((offer) => ({
                ...offer,
                accountUids: offer.accountUids.filter((value) => value !== uid),
            }))
        );

    return (
        <FieldArray name="accounts">
            {(arrayHelpers) => (
                <div className="flex flex-col gap-4">
                    {formik.values.accounts.map((account, index) => {
                        const accountErrors = formik.errors.accounts;
                        const handleError =
                            Array.isArray(accountErrors) && typeof accountErrors[index] === 'object'
                                ? (accountErrors[index] as { handle?: string }).handle
                                : undefined;
                        const accountsTouched = formik.touched.accounts;
                        const handleTouched =
                            Array.isArray(accountsTouched) && typeof accountsTouched[index] === 'object'
                                ? (accountsTouched[index] as { handle?: boolean }).handle
                                : false;
                        return (
                            <div key={index} className="flex flex-col gap-2">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="w-[10rem] notranslate" translate="no">
                                        <Select
                                            aria-label="Platform"
                                            menuPosition="fixed"
                                            captureMenuScroll={false}
                                            styles={waveSelectStyles}
                                            options={PLATFORM_OPTIONS}
                                            value={PLATFORM_OPTIONS.find((o) => o.value === account.platform)}
                                            onChange={(option) =>
                                                formik.setFieldValue(
                                                    `accounts.${index}.platform`,
                                                    (option as PlatformOption).value
                                                )
                                            }
                                        />
                                    </div>
                                    {formik.values.accounts.length > 1 && (
                                        <button
                                            type="button"
                                            className="wm-btn wm-btn--link"
                                            onClick={() => {
                                                arrayHelpers.remove(index);
                                                pruneBundleOfferAccount(account.uid);
                                            }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                                <Field name={`accounts.${index}.handle`}>
                                    {({ field }: FieldProps) => (
                                        <input
                                            className="wm-input"
                                            type="text"
                                            aria-label={`${platformLabel(account.platform)} handle or profile URL`}
                                            placeholder="@handle or profile URL"
                                            {...field}
                                        />
                                    )}
                                </Field>
                                {handleTouched && handleError && (
                                    <span className="text-xs text-wm-danger">{handleError}</span>
                                )}
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        className="wm-btn wm-btn--ghost"
                        onClick={() =>
                            // A new account inherits the mirrored section values while
                            // "same for all my accounts" is on.
                            arrayHelpers.push(
                                formik.values.sameForAllAccounts && formik.values.accounts.length > 0
                                    ? { ...emptyAccount(), ...accountSectionValues(formik.values.accounts[0]) }
                                    : emptyAccount()
                            )
                        }
                    >
                        Add another account
                    </button>
                    {typeof formik.errors.accounts === 'string' && (
                        <span className="text-xs text-wm-danger">{formik.errors.accounts}</span>
                    )}
                    <label className="wm-field">
                        <span className="wm-field__label">Any other socials? (Snapchat, Reddit, X…)</span>
                        <Field name="otherSocials">
                            {({ field }: FieldProps) => <textarea className="wm-input wm-textarea" {...field} />}
                        </Field>
                    </label>
                </div>
            )}
        </FieldArray>
    );
};

type WhereBasedStepProps = {
    location: LocationValue;
    onLocationChange: (value: LocationValue, locationId: number | null) => void;
};

export const WhereBasedStep = ({ location, onLocationChange }: WhereBasedStepProps) => (
    <div className="flex flex-col gap-4">
        <div>
            <LocationStep value={location} onChange={(next) => onLocationChange(next, locationIdOf(next))} />
            <FieldError name="locationId" />
        </div>
        <label className="wm-check">
            <Field type="checkbox" name="happyToTravel" />
            <span className="wm-check__box">
                <CheckIcon />
            </span>
            <span className="wm-check__text">I&apos;m happy to travel for campaigns</span>
        </label>
    </div>
);

export const accountLabel = (account: AccountValue, index: number) =>
    `${platformLabel(account.platform)} · ${account.handle.trim() || `Account ${index + 1}`}`;

type SetAccountField = (field: keyof AccountValue, value: unknown) => void;

const AccountFieldError = ({ index, field }: { index: number; field: keyof AccountValue }) => {
    const formik = useFormikContext<SignupFormValues>();
    const touched = getIn(formik.touched, `accounts.${index}.${field}`);
    const error = getIn(formik.errors, `accounts.${index}.${field}`);
    return touched && typeof error === 'string' ? <span className="text-xs text-wm-danger">{error}</span> : null;
};

type AccountSectionsProps = {
    renderAccount: (index: number, account: AccountValue, setField: SetAccountField) => ReactNode;
};

/**
 * Repeats a per-account section widget for every added account. With more than one
 * account, a "Same for all my accounts" toggle mirrors the first account's section
 * values onto the rest (and keeps mirroring while on); toggling it off leaves the
 * copies in place, each individually editable.
 */
export const AccountSections = ({ renderAccount }: AccountSectionsProps) => {
    const formik = useFormikContext<SignupFormValues>();
    const { accounts, sameForAllAccounts } = formik.values;
    const mirrored = sameForAllAccounts && accounts.length > 1;

    const setFieldFor =
        (index: number): SetAccountField =>
        (field, value) => {
            if (mirrored) {
                formik.setValues({
                    ...formik.values,
                    accounts: accounts.map((account) => ({ ...account, [field]: value })),
                });
            } else {
                formik.setFieldValue(`accounts.${index}.${field}`, value);
            }
        };

    const toggleSameForAll = (checked: boolean) => {
        const first = accountSectionValues(accounts[0]);
        formik.setValues({
            ...formik.values,
            sameForAllAccounts: checked,
            accounts: checked ? accounts.map((account) => ({ ...account, ...first })) : accounts,
        });
    };

    return (
        <div className="flex flex-col gap-6">
            {accounts.length > 1 && (
                <label className="wm-check">
                    <input
                        type="checkbox"
                        checked={sameForAllAccounts}
                        onChange={(event) => toggleSameForAll(event.target.checked)}
                    />
                    <span className="wm-check__box">
                        <CheckIcon />
                    </span>
                    <span className="wm-check__text">Same for all my accounts</span>
                </label>
            )}
            {(mirrored ? accounts.slice(0, 1) : accounts).map((account, index) => (
                <div key={index} className="flex flex-col gap-4">
                    {accounts.length > 1 && (
                        <FieldLabel>{mirrored ? 'All my accounts' : accountLabel(account, index)}</FieldLabel>
                    )}
                    {renderAccount(index, account, setFieldFor(index))}
                </div>
            ))}
        </div>
    );
};

/** Per-account based-in country plus top audience territories (country Location rows). */
export const AccountTerritoriesStep = () => (
    <AccountSections
        renderAccount={(index, account, setField) => (
            <>
                <div className="wm-field">
                    <label className="wm-field__label" htmlFor={`based-in-${index}`}>
                        <span>
                            Country you&apos;re based in <span className="text-wm-danger">*</span>
                        </span>
                    </label>
                    <CountrySelect
                        inputId={`based-in-${index}`}
                        placeholder="Select a country"
                        value={account.basedInCountry}
                        onChange={(option) => setField('basedInCountry', option)}
                    />
                    <AccountFieldError index={index} field="basedInCountry" />
                </div>
                <div className="wm-field">
                    <label className="wm-field__label" htmlFor={`audience-territories-${index}`}>
                        <span>
                            Top {AUDIENCE_TERRITORIES_COUNT} audience territories{' '}
                            <span className="text-wm-danger">*</span>
                        </span>
                    </label>
                    <CountryMultiSelect
                        inputId={`audience-territories-${index}`}
                        placeholder={`Select ${AUDIENCE_TERRITORIES_COUNT} countries`}
                        value={account.audienceTerritories}
                        onChange={(options) => setField('audienceTerritories', options)}
                    />
                    <span className="wm-field__hint">The countries most of this account&apos;s audience is in.</span>
                    <AccountFieldError index={index} field="audienceTerritories" />
                </div>
            </>
        )}
    />
);

/** Per-account edit-niche chips, capped at MAX_ACCOUNT_NICHES; a fan-page pick asks which artist. */
export const AccountNichesStep = ({ options }: { options: ChipOption<string>[] }) => (
    <AccountSections
        renderAccount={(index, account, setField) => (
            <>
                <div>
                    <ChipMultiSelect
                        options={options}
                        values={account.editNiches}
                        onChange={(v) => setField('editNiches', v)}
                        maxValues={MAX_ACCOUNT_NICHES}
                    />
                    <span className="mt-2 block text-xs text-wm-ink-3">
                        {account.editNiches.length >= MAX_ACCOUNT_NICHES
                            ? `That's your ${MAX_ACCOUNT_NICHES}. Deselect one to swap it out.`
                            : `Pick up to ${MAX_ACCOUNT_NICHES}, so we know what this account actually edits.`}
                    </span>
                    <div className="mt-2">
                        <AccountFieldError index={index} field="editNiches" />
                    </div>
                </div>
                {account.editNiches.includes(ARTIST_FAN_PAGE_NICHE_VALUE) && (
                    <label className="wm-field">
                        <RequiredLabel>Which artist is the page about?</RequiredLabel>
                        <input
                            className="wm-input"
                            type="text"
                            value={account.fanPageArtist}
                            onChange={(event) => setField('fanPageArtist', event.target.value)}
                        />
                        <AccountFieldError index={index} field="fanPageArtist" />
                    </label>
                )}
            </>
        )}
    />
);

/** Per-account genre chips; an `other` pick asks for the specific genres. */
export const AccountGenresStep = ({ options }: { options: ChipOption<string>[] }) => (
    <AccountSections
        renderAccount={(index, account, setField) => (
            <>
                <ChipMultiSelect options={options} values={account.genres} onChange={(v) => setField('genres', v)} />
                {account.genres.includes(GENRE_OTHER_VALUE) && (
                    <label className="wm-field">
                        <span className="wm-field__label">Which other genres? (optional)</span>
                        <input
                            className="wm-input"
                            type="text"
                            value={account.genresOther}
                            onChange={(event) => setField('genresOther', event.target.value)}
                        />
                    </label>
                )}
            </>
        )}
    />
);

/** Per-account content languages; a searchable multi-select because the vocabulary is ~180 long. */
export const AccountLanguagesStep = ({ options, isLoading }: { options: LanguageOption[]; isLoading?: boolean }) => (
    <AccountSections
        renderAccount={(index, account, setField) => (
            <div className="wm-field">
                <label className="wm-field__label" htmlFor={`content-languages-${index}`}>
                    <span>
                        Languages you post in <span className="text-wm-danger">*</span>
                    </span>
                </label>
                <div translate="no" className="notranslate">
                    <Select
                        isMulti
                        // The menu is portaled, so an absolute menu scrolls the page on
                        // open and re-places after every pick while multi keeps it open.
                        menuPosition="fixed"
                        captureMenuScroll={false}
                        styles={waveSelectStyles}
                        inputId={`content-languages-${index}`}
                        isLoading={isLoading}
                        placeholder="Search languages"
                        options={options}
                        filterOption={filterLanguageOption}
                        value={options.filter((option) => account.contentLanguages.includes(option.value))}
                        onChange={(selected) =>
                            setField(
                                'contentLanguages',
                                ((selected as LanguageOption[]) ?? []).map((option) => option.value)
                            )
                        }
                    />
                </div>
                <AccountFieldError index={index} field="contentLanguages" />
            </div>
        )}
    />
);

/** Per-account fixed fee plus the creator-level CPM opt-in. */
export const RatesStep = () => (
    <div className="flex flex-col gap-6">
        <AccountSections
            renderAccount={(index, account, setField) => (
                <div className="flex items-start gap-3">
                    <label className="wm-field flex-1">
                        <RequiredLabel>Fee per post</RequiredLabel>
                        <input
                            className="wm-input"
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={account.rate}
                            onChange={(event) => setField('rate', event.target.value)}
                        />
                        <AccountFieldError index={index} field="rate" />
                    </label>
                    <div className="wm-field w-[8rem]">
                        <span className="wm-field__label">Currency</span>
                        <Select
                            aria-label="Currency"
                            menuPosition="fixed"
                            captureMenuScroll={false}
                            styles={waveSelectStyles}
                            options={RATE_CURRENCY_OPTIONS}
                            value={RATE_CURRENCY_OPTIONS.find((option) => option.value === account.rateCurrency)}
                            onChange={(option) =>
                                setField('rateCurrency', (option as (typeof RATE_CURRENCY_OPTIONS)[number]).value)
                            }
                        />
                    </div>
                </div>
            )}
        />
        <span className="text-xs text-wm-ink-3">
            Heads up: a fee that&apos;s set too high reduces how often you get booked.
        </span>
        <div>
            <label className="wm-check">
                <Field type="checkbox" name="openToCpm" aria-describedby="open-to-cpm-help" />
                <span className="wm-check__box">
                    <CheckIcon />
                </span>
                <span className="wm-check__text">I&apos;m open to CPM (view-based) campaigns</span>
            </label>
            <span id="open-to-cpm-help" className="mt-2 block text-xs text-wm-ink-3">
                Some campaigns pay per views. You earn for every 1,000 views your post gets. This will make you eligible
                for more campaigns.
            </span>
        </div>
    </div>
);

const BUNDLE_OFFER_FIELDS: (keyof BundleOfferValue)[] = ['numPosts', 'price', 'accountUids', 'expectedDurationDays'];

const BundleOfferFieldError = ({ index, field }: { index: number; field: keyof BundleOfferValue }) => {
    const formik = useFormikContext<SignupFormValues>();
    // The wizard's Continue gate marks the whole `bundleOffers` field touched rather than
    // each row's fields, so a boolean there counts as touching every row.
    const isTouched =
        getIn(formik.touched, 'bundleOffers') === true ||
        Boolean(getIn(formik.touched, `bundleOffers.${index}.${field}`));
    const error = getIn(formik.errors, `bundleOffers.${index}.${field}`);
    return isTouched && typeof error === 'string' ? <span className="text-xs text-wm-danger">{error}</span> : null;
};

// A blank row is valid on purpose (the step is skippable and opens blank), so blocking the
// add button on one renders no field error; this says why the button did nothing instead.
const ADD_BLOCKED_ON_BLANK_ROW_MESSAGE = 'Fill in this bundle before adding another.';

/**
 * Optional package deals ("3 posts for $150"), each bound to the accounts it covers.
 * Rows can be removed down to none, and the account picker only appears with more than
 * one account: with a single one the binding is implicit and the row stays unbound.
 */
export const BundleOffersStep = () => {
    const formik = useFormikContext<SignupFormValues>();
    const { accounts, bundleOffers } = formik.values;
    const canPickAccounts = accounts.length > 1;
    const [addBlockedMessage, setAddBlockedMessage] = useState('');

    // formik's own change handler parses a `type="number"` input into a number; the row
    // keeps the raw string so a cleared field stays blank and the string rules still apply.
    const handleNumericChange = (event: ChangeEvent<HTMLInputElement>) =>
        formik.setFieldValue(event.target.name, event.target.value);

    const toggleAccount = (index: number, uid: string) => {
        const selected = bundleOffers[index].accountUids;
        formik.setFieldValue(
            `bundleOffers.${index}.accountUids`,
            selected.includes(uid) ? selected.filter((value) => value !== uid) : [...selected, uid]
        );
    };

    return (
        <FieldArray name="bundleOffers">
            {(arrayHelpers) => {
                const handleAdd = async () => {
                    // Offering a second bundle while an earlier one is blank or broken makes
                    // no sense, so surface those rows' errors instead of appending.
                    const errors = await formik.validateForm();
                    const offerErrors = Array.isArray(errors.bundleOffers) ? errors.bundleOffers : [];
                    const unfinished = bundleOffers
                        .map((offer, index) => ({ offer, index }))
                        .filter(({ offer, index }) => isBundleOfferEmpty(offer) || Boolean(offerErrors[index]));
                    if (unfinished.length > 0) {
                        unfinished.forEach(({ index }) =>
                            BUNDLE_OFFER_FIELDS.forEach((field) =>
                                formik.setFieldTouched(`bundleOffers.${index}.${field}`, true, false)
                            )
                        );
                        // Rows with real errors explain themselves field by field; only a
                        // blank one needs telling.
                        const hasBlankRow = unfinished.some(({ index }) => !offerErrors[index]);
                        setAddBlockedMessage(hasBlankRow ? ADD_BLOCKED_ON_BLANK_ROW_MESSAGE : '');
                        return;
                    }
                    setAddBlockedMessage('');
                    arrayHelpers.push(emptyBundleOffer());
                };

                return (
                    <div className="flex flex-col gap-6">
                        {bundleOffers.map((offer, index) => (
                            <div key={index} className="flex flex-col gap-4">
                                <div className="flex items-center justify-between gap-3">
                                    {bundleOffers.length > 1 ? (
                                        <FieldLabel>{`Bundle ${index + 1}`}</FieldLabel>
                                    ) : (
                                        <span />
                                    )}
                                    <button
                                        type="button"
                                        className="wm-btn wm-btn--link"
                                        onClick={() => arrayHelpers.remove(index)}
                                    >
                                        Remove
                                    </button>
                                </div>
                                <div className="wm-field">
                                    <label className="wm-field__label" htmlFor={`bundle-num-posts-${index}`}>
                                        <span>
                                            Number of posts <span className="text-wm-ink-3">*</span>
                                        </span>
                                    </label>
                                    <Field name={`bundleOffers.${index}.numPosts`}>
                                        {({ field }: FieldProps) => (
                                            <input
                                                className="wm-input"
                                                id={`bundle-num-posts-${index}`}
                                                type="number"
                                                min={MIN_BUNDLE_POSTS}
                                                step="1"
                                                inputMode="numeric"
                                                {...field}
                                                onChange={handleNumericChange}
                                            />
                                        )}
                                    </Field>
                                    <BundleOfferFieldError index={index} field="numPosts" />
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="wm-field flex-1">
                                        <label className="wm-field__label" htmlFor={`bundle-price-${index}`}>
                                            <span>
                                                Price for the whole bundle <span className="text-wm-ink-3">*</span>
                                            </span>
                                        </label>
                                        <Field name={`bundleOffers.${index}.price`}>
                                            {({ field }: FieldProps) => (
                                                <input
                                                    className="wm-input"
                                                    id={`bundle-price-${index}`}
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    inputMode="decimal"
                                                    {...field}
                                                    onChange={handleNumericChange}
                                                />
                                            )}
                                        </Field>
                                        <BundleOfferFieldError index={index} field="price" />
                                    </div>
                                    <div className="wm-field w-[8rem]">
                                        <span className="wm-field__label">Currency</span>
                                        <Select
                                            aria-label="Currency"
                                            menuPosition="fixed"
                                            captureMenuScroll={false}
                                            inputId={`bundle-currency-${index}`}
                                            styles={waveSelectStyles}
                                            options={RATE_CURRENCY_OPTIONS}
                                            value={RATE_CURRENCY_OPTIONS.find(
                                                (option) => option.value === offer.currency
                                            )}
                                            onChange={(option) =>
                                                formik.setFieldValue(
                                                    `bundleOffers.${index}.currency`,
                                                    (option as (typeof RATE_CURRENCY_OPTIONS)[number]).value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="wm-field">
                                    <label className="wm-field__label" htmlFor={`bundle-duration-${index}`}>
                                        How long you&apos;d need to deliver it
                                    </label>
                                    <Field name={`bundleOffers.${index}.expectedDurationDays`}>
                                        {({ field }: FieldProps) => (
                                            <input
                                                className="wm-input"
                                                id={`bundle-duration-${index}`}
                                                type="number"
                                                min={MIN_BUNDLE_DURATION_DAYS}
                                                max={MAX_BUNDLE_DURATION_DAYS}
                                                step="1"
                                                inputMode="numeric"
                                                {...field}
                                                onChange={handleNumericChange}
                                            />
                                        )}
                                    </Field>
                                    <span className="wm-field__hint">
                                        Optional. Whole days, up to {MAX_BUNDLE_DURATION_DAYS}, from the go-ahead to the
                                        last post.
                                    </span>
                                    <BundleOfferFieldError index={index} field="expectedDurationDays" />
                                </div>
                                {canPickAccounts && (
                                    <div>
                                        <FieldLabel>Accounts this bundle covers</FieldLabel>
                                        <div className="flex flex-col gap-2">
                                            {accounts.map((account, accountIndex) => (
                                                <label key={account.uid} className="wm-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={offer.accountUids.includes(account.uid)}
                                                        onChange={() => toggleAccount(index, account.uid)}
                                                    />
                                                    <span className="wm-check__box">
                                                        <CheckIcon />
                                                    </span>
                                                    <span className="wm-check__text">
                                                        {accountLabel(account, accountIndex)}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        <div className="mt-2">
                                            <BundleOfferFieldError index={index} field="accountUids" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <button type="button" className="wm-btn wm-btn--ghost" onClick={handleAdd}>
                            Add another bundle
                        </button>
                        {addBlockedMessage && <span className="text-xs text-wm-danger">{addBlockedMessage}</span>}
                    </div>
                );
            }}
        </FieldArray>
    );
};

export const StayingInTouchStep = () => {
    const formik = useFormikContext<SignupFormValues>();
    return (
        <div className="flex flex-col gap-6">
            <div>
                <FieldLabel>How should we send you briefs?</FieldLabel>
                <ChipMultiSelect
                    options={NOTIFICATION_CHANNEL_OPTIONS}
                    values={formik.values.notificationChannels}
                    onChange={(v) => formik.setFieldValue('notificationChannels', v)}
                />
                <span className="mt-2 block text-xs text-wm-ink-3">
                    Your in-app inbox is always on. These are extra channels — change them anytime.
                </span>
            </div>
            <div>
                <FieldLabel>When are you usually free to post?</FieldLabel>
                <ChipMultiSelect
                    options={AVAILABILITY_OPTIONS}
                    values={formik.values.availability}
                    onChange={(v) => formik.setFieldValue('availability', v)}
                />
            </div>
        </div>
    );
};

export const TermsSection = () => {
    const formik = useFormikContext<SignupFormValues>();
    return (
        <>
            <label className="wm-check mt-4">
                <Field type="checkbox" name="termsAccepted" />
                <span className="wm-check__box">
                    <CheckIcon />
                </span>
                <span className="wm-check__text">
                    I have reviewed the{' '}
                    <a
                        href="https://thisisround.com/privacy-policy/"
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                    >
                        privacy policy
                    </a>{' '}
                    and agree to the{' '}
                    <a
                        href="https://thisisround.com/creator-terms-and-conditions/"
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                    >
                        terms and conditions
                    </a>
                    .
                </span>
            </label>
            {formik.touched.termsAccepted && typeof formik.errors.termsAccepted === 'string' && (
                <span className="text-xs text-wm-danger">{formik.errors.termsAccepted}</span>
            )}
        </>
    );
};
